import React from 'react';

import CancelConfirmDialog from './components/CancelConfirmDialog';
import MultiSelectList from './components/MultiSelectList';
import SearchableSelect from './components/SearchableSelect';
import { type CopSubscriptionConfig } from './CopSubscription.config';
import { useSubscriptionForm } from './hooks/useSubscriptionForm';
import {
    DEFAULT_BUTTON_LABELS,
    DEFAULT_CANCEL_DIALOG,
    DEFAULT_LABELS,
    DEFAULT_MESSAGES,
    DEFAULT_PLACEHOLDERS,
    NOSLA_LABEL,
    NOSLA_VALUE,
} from './SubscriptionForm.constants';

export interface SubscriptionFormProps {
    config?: CopSubscriptionConfig;
    subscriptionId?: string;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({ config = {}, subscriptionId }) => {
    const fields = config.subscriptionForm ?? [];
    const f = (i: number) => fields[i] ?? { label: '', placeholder: '' };
    const customerField = f(0), typeField = f(1), countryField = f(2), emailField = f(3), siteField = f(4);

    const requiredError = config.requiredErrorText ?? DEFAULT_MESSAGES.requiredError;
    const confirmLabel = config.confirmLabel ?? DEFAULT_BUTTON_LABELS.confirm;
    const cancelLabel = config.cancelLabel ?? DEFAULT_BUTTON_LABELS.cancel;

    const {
        showCancelConfirm, setShowCancelConfirm,
        customers, loadingInitial, loadingDependent, submitting,
        selectedCustomerId, setSelectedCustomerId,
        selectedType, setSelectedType,
        selectedCountry, setSelectedCountry,
        selectedUserIds, setSelectedUserIds,
        selectedSiteIds, setSelectedSiteIds,
        subscriptionTypeOptions, customerOptions, countryOptions, userOptions, siteOptions,
        isTypeInvalid, isCountryInvalid, isEmailInvalid, isSiteInvalid, isCustomerInvalid,
        showNoUsersMsg, showNoSitesMsg,
        handleCancelClick, handleSubmit,
        searchingCustomers, loadingMoreCustomers, hasMoreCustomers,
        setCustomerSearchTerm, loadMoreCustomers,
    } = useSubscriptionForm(config, subscriptionId);

    const err = (msg: string) => <p className="field-error"><span className="field-error__icon">i</span>{msg}</p>;
    const customerSelected = !!selectedCustomerId && selectedCustomerId !== NOSLA_VALUE;
    const emailDisabled = !customerSelected || loadingDependent || (!loadingDependent && userOptions.length === 0);
    const siteDisabled = !customerSelected || loadingDependent || (!loadingDependent && siteOptions.length === 0);

    return (
        <>
            <form onSubmit={handleSubmit}>

                <div className="form-group">
                    <label><span className="required-mark">*</span>{customerField.label || DEFAULT_LABELS.customerAccount}</label>
                    <SearchableSelect
                        options={customerOptions}
                        value={selectedCustomerId}
                        onChange={setSelectedCustomerId}
                        placeholder={customerField.placeholder || DEFAULT_PLACEHOLDERS.customerAccount}
                        loading={loadingInitial}
                        disabled={loadingInitial}
                        invalid={isCustomerInvalid}
                        pinnedOption={customers.length > 0 ? { value: NOSLA_VALUE, label: NOSLA_LABEL } : undefined}
                        onSearchChange={setCustomerSearchTerm}
                        onLoadMore={loadMoreCustomers}
                        loadingMore={loadingMoreCustomers}
                        hasMore={hasMoreCustomers}
                        searching={searchingCustomers}
                        required
                    />
                    {isCustomerInvalid && err(requiredError)}
                </div>

                <div className="form-group">
                    <label><span className="required-mark">*</span>{typeField.label || DEFAULT_LABELS.typeOfSubscription}</label>
                    <SearchableSelect
                        options={subscriptionTypeOptions}
                        value={selectedType}
                        onChange={setSelectedType}
                        placeholder={typeField.placeholder || DEFAULT_PLACEHOLDERS.typeOfSubscription}
                        invalid={isTypeInvalid}
                        required
                    />
                    {isTypeInvalid && err(requiredError)}
                </div>

                <div className="form-group">
                    <label><span className="required-mark">*</span>{countryField.label || DEFAULT_LABELS.country}</label>
                    <SearchableSelect
                        options={countryOptions}
                        value={selectedCountry}
                        onChange={setSelectedCountry}
                        placeholder={countryField.placeholder || DEFAULT_PLACEHOLDERS.country}
                        loading={loadingInitial}
                        disabled={loadingInitial}
                        invalid={isCountryInvalid}
                        required
                    />
                    {isCountryInvalid && err(requiredError)}
                </div>

                <div className="form-group">
                    <label><span className="required-mark">*</span>{emailField.label || DEFAULT_LABELS.customerEmailAddresses}</label>
                    <MultiSelectList
                        options={userOptions}
                        selectedValues={selectedUserIds}
                        onChange={setSelectedUserIds}
                        disabled={emailDisabled}
                        loading={loadingDependent}
                        invalid={isEmailInvalid}
                        placeholder={emailField.placeholder || DEFAULT_PLACEHOLDERS.customerEmailAddresses}
                        disabledMessage={DEFAULT_MESSAGES.selectCustomerFirst}
                    />
                    {showNoUsersMsg && err(config.customerEmailDisabledText ?? DEFAULT_MESSAGES.noUsersAssigned)}
                    {isEmailInvalid && err(requiredError)}
                </div>

                <div className="form-group">
                    <label><span className="required-mark">*</span>{siteField.label || DEFAULT_LABELS.siteIds}</label>
                    <MultiSelectList
                        options={siteOptions}
                        selectedValues={selectedSiteIds}
                        onChange={setSelectedSiteIds}
                        disabled={siteDisabled}
                        loading={loadingDependent}
                        invalid={isSiteInvalid}
                        placeholder={siteField.placeholder || DEFAULT_PLACEHOLDERS.siteIds}
                        disabledMessage={DEFAULT_MESSAGES.selectCustomerFirst}
                    />
                    {showNoSitesMsg && err(config.siteIdDisabledText ?? DEFAULT_MESSAGES.noSitesAssigned)}
                    {isSiteInvalid && err(requiredError)}
                </div>

                <div className="button-group">
                    <button type="button" className="btn btn-cancel" onClick={handleCancelClick} disabled={submitting}>
                        {cancelLabel}
                    </button>
                    <button type="submit" className="btn btn-add" disabled={submitting}>
                        {submitting ? '...' : confirmLabel}
                    </button>
                </div>
            </form>

            {showCancelConfirm && (
                <CancelConfirmDialog
                    message={config.cancel?.message ?? DEFAULT_CANCEL_DIALOG.message}
                    subtext={config.cancel?.subtext ?? DEFAULT_CANCEL_DIALOG.subtext}
                    yesLabel={config.cancel?.yesLabel ?? DEFAULT_CANCEL_DIALOG.yesLabel}
                    noLabel={config.cancel?.noLabel ?? DEFAULT_CANCEL_DIALOG.noLabel}
                    yesUrlPath={config.cancel?.yesUrl}
                    onNo={() => setShowCancelConfirm(false)}
                />
            )}
        </>
    );
};

export default SubscriptionForm;
